import { useMutation } from '@tanstack/react-query';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { switchTenant } from '@/features/auth/auth-api';
import { useAuth } from '@/hooks/use-auth';
import { queryClient } from '@/lib/query-client';
import { errorMessage } from '@/lib/error-message';

export function TenantSwitcher() {
  const { tenant, memberships, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: switchTenant,
    onSuccess: async (session) => {
      await queryClient.resetQueries();
      toast.success(`Empresa ativa: ${session.tenant?.tradeName ?? ''}`);
      void navigate('/dashboard');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (memberships.length <= 1 && !isSuperAdmin) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="max-w-56 justify-between gap-2"
          disabled={mutation.isPending}
        >
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{tenant?.tradeName ?? 'Selecionar empresa'}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Suas empresas</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.id}
            onSelect={() => m.tenant.id !== tenant?.id && mutation.mutate(m.tenant.id)}
          >
            <span className="truncate">{m.tenant.tradeName}</span>
            {m.tenant.id === tenant?.id ? <Check className="ml-auto size-4" /> : null}
          </DropdownMenuItem>
        ))}
        {memberships.length === 0 ? (
          <DropdownMenuItem disabled>Nenhum vínculo direto</DropdownMenuItem>
        ) : null}
        {isSuperAdmin ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => void navigate('/admin/empresas')}>
              Administrar empresas
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
